import { Avatar } from "@etframework/ui.components";
import {
  ItemDirective,
  ItemsDirective,
  TimelineComponent,
} from "@syncfusion/ej2-react-layouts";
import moment from "moment-timezone"; // needs to imported event though it is not used in order to use `moment.tz()`
import "moment/dist/locale/es-us";
import { useTranslation } from "react-i18next";
import { Constants } from "../../types/constants";
import { DateFormats } from "../../types/DateFormats";
import { formatDateFromNow, isNullOrUndefined } from "../../utils/helpers";

export const ActivityTimeline = ({ activityLogs }) => {
  const { t, i18n } = useTranslation(["Overlay", "Shared"]);

  const _formatDate = (date) => {
    return formatDateFromNow(
      date,
      i18n.language,
      DateFormats.ShortDateTimeFormat.momentJsFormat
    );
  };

  const setIconClass = (field) => {
    switch (field) {
      case "Client":
        return "fa-building-user";
      case "Assignee":
        return "fa-circle-user";
      case "DueDate":
      case "StartDate":
      case "TaskTaxReturnInfo.TaxDeadline":
      case "TaskTaxReturnInfo.ExtendedTaxDeadline":
      case "TaskTaxReturnInfo.PeriodEnd":
        return "fa-calendar-day";
      case "Tags":
        return "fa-tags";
      case "Collaborators":
        return "fa-users";
      case "Description":
        return "fa-list-ul";
      case "Stage":
      case "TaskTaxReturnInfo.FilingStatus":
        return "fa-list-check";
      default:
        return "fa-clipboard-list-check";
    }
  };

  const ShowArrow = [
    Constants.LogAction.Changed,
    Constants.LogAction.ChangedInBulk,
    "",
  ];

  const ExcludeTheRemovedValue = ["TaskTaxReturnInfo.ExtendedTaxDeadline"];

  const empty = <span className="empty">{t("ActivityTab.Empty")}</span>;

  const arrowTemplate = (FieldModified, OriginalValue, NewValue) => {
    const item1 = OriginalValue
      ? FormatActivityLogValue(FieldModified, OriginalValue, t)
      : empty;

    const item2 = NewValue
      ? FormatActivityLogValue(FieldModified, NewValue, t)
      : empty;

    return (
      <div className="d-flex justify-content-start gap-2">
        <div className="at-content" title={item1}>
          {item1}
        </div>
        <div>→</div>
        <div className="at-content" title={item2}>
          {item2}
        </div>
      </div>
    );
  };

  const detailsTemplate = ({
    Action,
    FieldModified,
    OriginalValue,
    NewValue,
  }) => {
    const wrapper = (children) => (
      <span className="al-details">{children}</span>
    );

    if (!OriginalValue && !NewValue) {
      return null;
    } else if (ShowArrow.includes(Action)) {
      return wrapper(arrowTemplate(FieldModified, OriginalValue, NewValue));
    } else {
      return wrapper(
        <>
          {OriginalValue &&
            !ExcludeTheRemovedValue.includes(FieldModified) &&
            FormatActivityLogValue(FieldModified, OriginalValue, t)}
          {NewValue && FormatActivityLogValue(FieldModified, NewValue, t)}
        </>
      );
    }
  };

  const activityLogTemplate = (activityLog) => {
    const { Action, FieldModified, UserFullName, CreatedDate, IsSystemLog } =
      activityLog;

    return (
      <>
        <div className="activitylog">
          <div className="al-content">
            <div className="al-content-column d-flex align-items-center">
              <Avatar content={UserFullName} />
              <div className="al-message ms-2 fw-600">
                <span className="al-action">
                  {Action &&
                    GetActivityLogText(
                      t,
                      Action,
                      FieldModified,
                      IsSystemLog,
                      UserFullName
                    )}{" "}
                </span>
              </div>
            </div>
          </div>
          {detailsTemplate(activityLog)}
          <span className="al-date et-darkest-gray">
            {CreatedDate && _formatDate(CreatedDate)}
          </span>
        </div>
      </>
    );
  };

  return (
    <div className="al-timeline-container ">
      <div id="timeline" className="">
        <TimelineComponent>
          <ItemsDirective>
            {activityLogs &&
              activityLogs?.map((al) => (
                <ItemDirective
                  dotCss={`fa-regular ${setIconClass(
                    al.FieldModified
                  )} al-timeline-dot`}
                  content={() => activityLogTemplate(al)}
                  key={al.Id}
                />
              ))}
          </ItemsDirective>
        </TimelineComponent>
      </div>
    </div>
  );
};

export const GetActivityLogText = (
  t,
  Action,
  FieldModified,
  IsSystemLog,
  UserFullName
) => {
  let text = "";

  if (UserFullName) text += UserFullName + " ";

  text += t?.(`ActivityTab.${Action}`, {
    field: GetActivityLogField(t, FieldModified, Action),
    context: IsSystemLog ? "system" : null,
  });

  return text;
};

export const GetActivityLogField = (t, FieldModified, Action) => {
  if (FieldModified.startsWith("Custom:")) {
    var [Group, Label] = FieldModified.replace("Custom:", "").split("[[OF]]");
    return `${Label} ${t?.("ActivityTab.in")} ${Group}`;
  } else {
    return t?.("ActivityTab." + FieldModified, {
      context: Action,
    })?.toLocaleLowerCase();
  }
};

export const FormatActivityLogValue = (field, value, t) => {
  if (field === "TaskTaxReturnInfo.FilingStatus") return t(value);

  if (isNullOrUndefined(value)) return value;

  if (
    value == "true" ||
    value == "True" ||
    value == "false" ||
    value == "False"
  )
    return t?.("ActivityTab." + value);
  else if (!isNaN(value))
    return value /* moment will mark any int as a valid date */;
  else if (moment(value, moment.ISO_8601, true).isValid())
    return moment(value).format(DateFormats.ShortDateFormat.momentJsFormat);
  else return value;
};
